import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { isoDate, jsonRecord, trackedSkuSourceSchema } from "./primitives"
import { runItemVerdictReasonSchema, runItemVerdictSchema } from "./runs"

// ── TrackedSku ─────────────────────────────────────────────────────────────

export const trackedSkuSchema = z.object({
  id: z.number().int(),
  configSku: z.string(),
  marketplace: marketplaceIdSchema,
  marketplaceStatus: z.string(),
  category: z.string().nullable(),
  variantCount: z.number().int().nullable(),
  source: trackedSkuSourceSchema,
  createdAt: isoDate,
  lastSyncedAt: isoDate,
  marketplaceCreatedAt: z.string().nullable(),
  generatedAt: z.string().nullable(),
  meta: jsonRecord,
})
export type TrackedSku = z.infer<typeof trackedSkuSchema>

// A tracked SKU plus its run-scoped state: the OK/NOK verdict + reasons and the
// per-run set-aside timestamp (null = active). This is the shape the run-detail
// reads (product previews, feed preview) actually ship — `trackedSkuSchema` is
// the bare catalog row; this adds the per-run columns from `listing_run_items`.
export const runSkuRecordSchema = trackedSkuSchema.extend({
  verdict: runItemVerdictSchema.nullable(),
  verdictAt: z.string().nullable().optional(),
  verdictReasons: z.array(runItemVerdictReasonSchema).nullable(),
  setAsideAt: z.string().nullable(),
  /** Why this config was parked, as the operator typed it (`--reason`). Null
   *  while it is active, and also on rows parked before the reason was
   *  recorded. Carried alongside the timestamp so a reader can say WHY a config
   *  is missing from the feed without a second query. */
  setAsideReason: z.string().nullable(),
})
export type RunSkuRecord = z.infer<typeof runSkuRecordSchema>

export const upsertTrackedSkuBodySchema = z.object({
  configSku: z.string().min(1),
  marketplace: marketplaceIdSchema,
  marketplaceStatus: z.string().optional(),
  category: z.string().optional(),
  variantCount: z.number().int().optional(),
  source: trackedSkuSourceSchema,
  listingRunId: z.number().int().optional(),
  marketplaceCreatedAt: z.string().optional(),
  meta: jsonRecord.optional(),
})
export type UpsertTrackedSkuBody = z.infer<typeof upsertTrackedSkuBodySchema>

export const compareSkusBodySchema = z.object({
  skus: z.array(z.string()).min(1),
  marketplace: marketplaceIdSchema,
})
export type CompareSkusBody = z.infer<typeof compareSkusBodySchema>

export const compareSkusResultSchema = z.object({
  ok: z.array(z.string()),
  alreadyListed: z.array(
    z.object({ sku: z.string(), status: z.string(), source: trackedSkuSourceSchema })
  ),
  pending: z.array(z.object({ sku: z.string(), status: z.string() })),
  rejected: z.array(z.object({ sku: z.string(), status: z.string() })),
})
export type CompareSkusResult = z.infer<typeof compareSkusResultSchema>

export const registerTrackedSkusBodySchema = z.object({
  skus: z.array(upsertTrackedSkuBodySchema).min(1),
})
export type RegisterTrackedSkusBody = z.infer<typeof registerTrackedSkusBodySchema>

// ── Per-run set-aside bucket (issue #56) ────────────────────────────────────
//
// Park configs whose source data is genuinely missing so the rest of a run can
// build and list while the data team fills the gap; restore reverses it. Both
// are POSTs scoped to one run by slug.

// `configSkus` is required + non-empty. `allDataGaps` is a server-side
// convenience: bucket every config currently carrying a fixable gap in one
// shot, so `configSkus` may be omitted when it is set. When both are given,
// `configSkus` adds to the gap set. `reason` is required so the export carries
// an explanation for the data team (free text; the operator's note).
// Bound the durable note body: free text, but capped so an oversized payload
// can't be persisted (security: assume hostile input at the boundary). Shared by
// the set-aside `note` and the standalone set-aside-note edit/clear endpoint.
// Empty string ("") is meaningful on the edit endpoint (a clear); on set-aside
// it's just "no note supplied", so set-aside trims-and-drops empties.
export const SET_ASIDE_NOTE_MAX_CHARS = 2000

export const setAsideRunItemsBodySchema = z
  .object({
    configSkus: z.array(z.string().min(1)).default([]),
    reason: z.string().trim().min(1),
    allDataGaps: z.boolean().default(false),
    // Optional durable per-product note (issue #122) recorded for every config
    // actually parked, keyed on (configSku, marketplace) so it survives re-runs.
    // Distinct from `reason`, which stays the per-run action reason on the item.
    note: z.string().trim().max(SET_ASIDE_NOTE_MAX_CHARS).optional(),
  })
  .strict()
  .refine((body) => body.allDataGaps || body.configSkus.length > 0, {
    message: "Provide configSkus or set allDataGaps",
    path: ["configSkus"],
  })
export type SetAsideRunItemsBody = z.infer<typeof setAsideRunItemsBodySchema>

export const restoreRunItemsBodySchema = z
  .object({
    configSkus: z.array(z.string().min(1)).min(1),
  })
  .strict()
export type RestoreRunItemsBody = z.infer<typeof restoreRunItemsBodySchema>

// Response for both set-aside and restore: which configs the operation actually
// affected, and which requested SKUs weren't on the run (so the caller can flag
// a typo instead of a silent no-op).
export const setAsideRunItemsResponseSchema = z.object({
  slug: z.string(),
  setAside: z.array(z.string()),
  unknown: z.array(z.string()),
})
export type SetAsideRunItemsResponse = z.infer<typeof setAsideRunItemsResponseSchema>

export const restoreRunItemsResponseSchema = z.object({
  slug: z.string(),
  restored: z.array(z.string()),
  unknown: z.array(z.string()),
})
export type RestoreRunItemsResponse = z.infer<typeof restoreRunItemsResponseSchema>

// One parked config in a run's set-aside bucket: the config plus WHY it was
// parked, WHO parked it, and WHEN — the read side of set-aside. `name` is the
// catalog-mirror name (null when the mirror hasn't seen the config). Powers the
// run page's set-aside drawer; restore stays a CLI action.
// One immutable version of a product's durable set-aside note (issue #122): the
// text plus who wrote it and when. The store is append-only, so "current" is the
// newest version and the full list is the edit history — that's how "restoring a
// product does not silently destroy its note history" holds (nothing is ever
// overwritten). Defined here so `runSetAsideItemSchema` can embed the current one.
export const setAsideNoteSchema = z.object({
  note: z.string(),
  author: z.string(),
  updatedAt: z.string(),
})
export type SetAsideNote = z.infer<typeof setAsideNoteSchema>

export const runSetAsideItemSchema = z.object({
  configSku: z.string(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  reason: z.string(),
  verdictReasons: z.array(runItemVerdictReasonSchema),
  setAsideBy: z.string(),
  setAsideAt: z.string(),
  // The DURABLE per-product set-aside note (issue #122), keyed on
  // (configSku, marketplace) — survives re-runs, unlike `reason` (the per-run
  // action reason above). Null when the product has no active note. The web
  // drill-down edits this via the set-aside-note endpoint.
  note: setAsideNoteSchema.nullable(),
})
export type RunSetAsideItem = z.infer<typeof runSetAsideItemSchema>

// The run's set-aside bucket, newest-parked first. O(parked), not O(configs).
export const runSetAsideListResponseSchema = z.object({
  slug: z.string(),
  items: z.array(runSetAsideItemSchema),
})
export type RunSetAsideListResponse = z.infer<typeof runSetAsideListResponseSchema>

// ── Durable set-aside note write/read (issue #122) ──────────────────────────
//
// Set / edit / clear a product's durable note. `note` is the new body; ""
// records a clear (the note goes inactive but its history is kept). The
// marketplace is resolved from the run slug in the path — the note itself is
// run-independent.
export const setAsideNoteBodySchema = z
  .object({
    configSku: z.string().min(1),
    note: z.string().trim().max(SET_ASIDE_NOTE_MAX_CHARS),
  })
  .strict()
export type SetAsideNoteBody = z.infer<typeof setAsideNoteBodySchema>

// The durable note after a write (or a plain read): the current active note
// (null when cleared / never set) and the full version history, newest first.
export const setAsideNoteResponseSchema = z.object({
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  configSku: z.string(),
  note: setAsideNoteSchema.nullable(),
  history: z.array(setAsideNoteSchema),
})
export type SetAsideNoteResponse = z.infer<typeof setAsideNoteResponseSchema>

// ── Run gap export (issue #56) ──────────────────────────────────────────────
//
// One row per config that carries a fixable gap (the data team's worklist),
// including the set-aside ones (flagged via `setAside`). The CLI serializes
// this to CSV.
export const runGapExportRowSchema = z.object({
  configSku: z.string(),
  name: z.string(),
  ean: z.string(),
  // Distinct required-attribute codes the config is missing (from the verdict).
  missingAttributes: z.array(z.string()),
  // Distinct gap reason codes (e.g. nutrition_table_missing, missing_required).
  gapReasons: z.array(z.string()),
  // Whether this config is currently parked in the set-aside bucket.
  setAside: z.boolean(),
  // Deep link to the product source (Magento) so the data team can open it.
  sourceUrl: z.string(),
})
export type RunGapExportRow = z.infer<typeof runGapExportRowSchema>

export const runGapExportResponseSchema = z.object({
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  rows: z.array(runGapExportRowSchema),
})
export type RunGapExportResponse = z.infer<typeof runGapExportResponseSchema>

// ── Run category facets ─────────────────────────────────────────────────────

/** The distinct categories present in a run, with per-category config counts,
 *  for the run page's category filters. Marketplace categories are the assigned
 *  marketplace (e.g. Mirakl) leaves resolved to human labels; Magento categories
 *  are the source `category_leaf` (with the full breadcrumb for disambiguation).
 *  Computed over the whole run (not a page), so the dropdowns list everything. */
export const runFacetsResponseSchema = z.object({
  marketplaceCategories: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      count: z.number().int().nonnegative(),
    })
  ),
  magentoCategories: z.array(
    z.object({
      leaf: z.string(),
      breadcrumb: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
  ),
})
export type RunFacetsResponse = z.infer<typeof runFacetsResponseSchema>
